import { Scale, FileText, Shield, AlertTriangle, Clock, Mail } from "lucide-react";
import { Card } from "@/components/ui/card";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Scale className="h-4 w-4" />
              Legal Agreement
            </div>
            <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
            <p className="text-xl text-muted-foreground">
              Please read these terms carefully before using TrueTrucker IFTA Pro
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>

          {/* Agreement */}
          <Card className="p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <FileText className="h-6 w-6 text-primary" />
              Agreement to Terms
            </h2>
            <p className="text-muted-foreground mb-4">
              By accessing and using TrueTrucker IFTA Pro ("the Service"), you accept and agree to be bound by the terms and provision of this agreement.
            </p>
            <div className="bg-muted/50 border-l-4 border-primary p-4 rounded">
              <p className="text-sm">
                <strong>Important:</strong> If you do not agree to abide by the above, please do not use this service.
              </p>
            </div>
          </Card>

          {/* Service Description */}
          <Card className="p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-4">Service Description</h2>
            <div className="space-y-4">
              <p className="text-muted-foreground">
                TrueTrucker IFTA Pro provides IFTA (International Fuel Tax Agreement) compliance tools including:
              </p>
              <ul className="text-sm text-muted-foreground space-y-2 ml-6">
                <li>• Automated mileage tracking and fuel tax calculations</li>
                <li>• Quarterly IFTA report generation</li>
                <li>• Receipt scanning and data management</li>
                <li>• Audit defense tools and documentation</li>
                <li>• Professional consulting and support services</li>
              </ul>
            </div>
          </Card>

          {/* User Responsibilities */}
          <Card className="p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" />
              User Responsibilities
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Account Security</h3>
                <p className="text-sm text-muted-foreground">
                  You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Data Accuracy</h3>
                <p className="text-sm text-muted-foreground">
                  You must provide accurate, complete, and up-to-date information. You are responsible for verifying the accuracy of all calculations and reports generated by the Service.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Lawful Use</h3>
                <p className="text-sm text-muted-foreground">
                  You agree to use the Service only for lawful purposes and in accordance with all applicable local, state, and federal laws and regulations.
                </p>
              </div>
            </div>
          </Card>

          {/* Service Availability */}
          <Card className="p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <Clock className="h-6 w-6 text-primary" />
              Service Availability & Modifications
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Uptime & Maintenance</h3>
                <p className="text-sm text-muted-foreground">
                  While we strive for 99.9% uptime, we do not guarantee uninterrupted service. We may perform scheduled maintenance with advance notice.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Service Updates</h3>
                <p className="text-sm text-muted-foreground">
                  We reserve the right to modify, update, or discontinue features of the Service with reasonable notice to users.
                </p>
              </div>
            </div>
          </Card>

          {/* Payment Terms */}
          <Card className="p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-4">Payment & Subscription Terms</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Billing</h3>
                <p className="text-sm text-muted-foreground">
                  Subscription fees are billed monthly or annually in advance. All fees are non-refundable except as required by law.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Price Changes</h3>
                <p className="text-sm text-muted-foreground">
                  We may change pricing with 30 days' notice. Current subscribers will maintain their pricing until the next renewal cycle.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Cancellation</h3>
                <p className="text-sm text-muted-foreground">
                  You may cancel your subscription at any time. Service continues until the end of your current billing period.
                </p>
              </div>
            </div>
          </Card>

          {/* Limitations */}
          <Card className="p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-primary" />
              Limitations of Liability
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Tax Compliance Disclaimer</h3>
                <p className="text-sm text-muted-foreground">
                  TrueTrucker IFTA Pro is a tool to assist with IFTA compliance. Users remain solely responsible for the accuracy of their tax filings and compliance with all applicable regulations.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Service Limitations</h3>
                <p className="text-sm text-muted-foreground">
                  We provide the Service "as is" without warranties of any kind. We are not liable for any indirect, incidental, or consequential damages.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Maximum Liability</h3>
                <p className="text-sm text-muted-foreground">
                  Our total liability to you for any claims related to the Service shall not exceed the amount you paid for the Service in the 12 months preceding the claim.
                </p>
              </div>
            </div>
          </Card>

          {/* Intellectual Property */}
          <Card className="p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-4">Intellectual Property</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Our Rights</h3>
                <p className="text-sm text-muted-foreground">
                  All content, features, and functionality of the Service are owned by TrueTrucker and protected by copyright, trademark, and other intellectual property laws.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Your Data</h3>
                <p className="text-sm text-muted-foreground">
                  You retain ownership of all data you input into the Service. We do not claim ownership of your business or tax information.
                </p>
              </div>
            </div>
          </Card>

          {/* Termination */}
          <Card className="p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-4">Termination</h2>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Either party may terminate this agreement at any time. Upon termination:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6">
                <li>• Your access to the Service will be discontinued</li>
                <li>• You may download your data for 30 days after termination</li>
                <li>• We may delete your account data after the 30-day period</li>
                <li>• All obligations under these terms survive termination</li>
              </ul>
            </div>
          </Card>

          {/* Governing Law */}
          <Card className="p-8 mb-8">
            <h2 className="text-2xl font-semibold mb-4">Governing Law & Disputes</h2>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                These terms are governed by the laws of the United States and the state where TrueTrucker is incorporated. 
                Any disputes will be resolved through binding arbitration in accordance with commercial arbitration rules.
              </p>
            </div>
          </Card>

          {/* Contact */}
          <Card className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Contact Information</h2>
            <p className="text-muted-foreground mb-6">
              Questions about these Terms of Service? Contact our legal team.
            </p>
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-primary" />
              <div>
                <p className="font-semibold">Legal & Terms Questions</p>
                <p className="text-sm text-muted-foreground">support@true-trucker-ifta-pro.com</p>
              </div>
            </div>
          </Card>

          {/* Back to Home */}
          <div className="text-center mt-12">
            <a 
              href="/" 
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              ← Back to TrueTrucker IFTA Pro
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;